/// <reference types="geojson" />

// Import enum types
import type {
  CheckinVersion,
  ClothingSize,
  ClothingType,
  CollaboratorGender,
  CollaboratorRole,
  CollaboratorStatus,
  ContactRole,
  ContactSubrole,
  ContractType,
  CoproApprovalStatus,
  DesignStatus,
  EquipmentType,
  ImplementationTypology,
  LaunchClearanceStatus,
  LeaseContractType,
  NoGradeReason,
  PlanningOptimizationStatus,
  ProfessionalStatus,
  ProjectTypology,
  ResolutionStatus,
  SecurisationType,
  UnavailabityReason,
  WeekDay,
  WorkShiftClockAction,
} from './enums/index.js'

import type { OpeningEventMethod, OpeningSessionStatus } from './types/openingEvents.types.js'

// Base type for data with timestamps
export interface TimestampsAndID {
  id: number

  createdAt: string
  updatedAt: string
}

// User data
export interface User extends TimestampsAndID {
  email: string
  firstName: string
  lastName: string
  isReliable: boolean
  isBanned: boolean
  unBanAmount: number | null
  worksConcilId: number | null
  storeId: number | null
  socketToken: string | null
  socketAiUrl: string | null
  socketStreamUrl: string | null
  socketUrl: string | null
  streamUrl: string | null
}

// Admin data
export interface Admin extends TimestampsAndID {
  email: string
  firstName: string
  lastName: string
  adminRights: number
  isDeleted: boolean
  socketToken: string | null
  socketAiUrl: string | null
  socketStreamUrl: string | null
  socketUrl: string | null
  streamUrl: string | null
  stores?: Store[] // Associated stores for this admin
}

// Type guards to differentiate between User and Admin
export const isUser = (user: User | Admin): user is User => !('adminRights' in user)

export const isAdmin = (user: User | Admin): user is Admin => 'adminRights' in user

// User action log
export interface UserActionLog {
  id: number
  createdAt: string
  userId: number
  adminId: number | null
  action: string
  detail: string
  userSnapshot: Record<string, unknown>
  user?: User | null
  admin?: Admin | null
}

// Store action log
export interface StoreActionLog {
  id: number
  createdAt: string
  storeId: number
  adminId: number | null
  action: string
  detail: string
  storeSnapshot: Record<string, unknown>
  store?: Store | null
  admin?: Admin | null
}

// Product action log
export interface ProductActionLog {
  id: number
  createdAt: string
  productId: number
  adminId: number | null
  action: string
  detail: string
  productSnapshot: Record<string, unknown>
  product?: Product | null
  admin?: Admin | null
}

export interface AttachmentFile {
  name: string
  extname: string
  size: number
  mimeType: string
  url?: string // Depends on preComputeUrl from the backend
}

// Category data
export interface Category extends TimestampsAndID {
  name: string
  image?: AttachmentFile
  categoryId: number | null
  isSubCategory: boolean
  description?: string
  rank: number
}

export interface InternalCategory extends TimestampsAndID {
  name: string
  trackDlc: boolean
  products: number[]
}

// Reappro Category data
export interface ReapproCategory extends TimestampsAndID {
  name: string
  daysBefore: number
  coverageRate: number
  dispatchDaysBefore: number | null
  dispatchCoverageRate: number | null
  computeHour: string // "HH:mm"
  sendHour: string
  logHour: string
  dispatchComputeHour: string | null
  dispatchSendHour: string | null
  products: number[]
  isDispatch: boolean
  reapproCategoryGroupId: number | null
}

export type ReapproAlgorithm = 'VMAX' | 'VMJ' | 'STOCK_PROJETE'
export type ProductReapproAlgorithm = ReapproAlgorithm | 'STOCK_MINIMUM'
export type ReaproPeriode = '3J' | '5J' | '7J'

export const REAPPRO_ALGORITHM_IDS = {
  VMAX: 1,
  VMJ: 2,
  STOCK_PROJETE: 3,
} as const

export type ReapproAlgorithmId = (typeof REAPPRO_ALGORITHM_IDS)[ReapproAlgorithm]

type Invert<T extends Record<PropertyKey, PropertyKey>> = {
  [K in keyof T as T[K]]: K
}

export const REAPPRO_ALGORITHM_BY_ID = Object.fromEntries(
  Object.entries(REAPPRO_ALGORITHM_IDS).map(([k, v]) => [v, k]),
) as Invert<typeof REAPPRO_ALGORITHM_IDS>

export interface ReapproAlgorithmParam extends TimestampsAndID {
  algorithm: ReapproAlgorithm
  daysBetweenDeliveries: number | null
}

export interface ReapproAlgorithmParamUpdate {
  id: ReapproAlgorithmId
  daysBetweenDeliveries: number | null
}

export interface StoreReapproCategoryAlgorithm extends TimestampsAndID {
  storeId: number
  reapproCategoryId: number
  algorithm: ReapproAlgorithm
  periode: ReaproPeriode
  coefSecu: number
  coefWeekend: number
  coverageRate: number | null
  dispatchCoverageRate: number | null
}

export interface ReapproCategoryAlgorithmUpdate {
  storeId: number
  reapproCategoryId: number
  algorithm: ReapproAlgorithm
  periode: ReaproPeriode
  coefSecu: number
  coefWeekend: number
  coverageRate?: number | null
  dispatchCoverageRate?: number | null
}

export type ReapproNeedAdjustmentScope = 'category' | 'store'

export interface ReapproNeedAdjustmentDTO {
  id: number
  reapproCategoryId: number
  scope: ReapproNeedAdjustmentScope
  storeId: number | null
  deliveryDate: string // ISO YYYY-MM-DD
  multiplier: number // [0, 3]
}

export interface ReapproProductPreview {
  productId: number
  reference: string
  effectiveAlgorithm: ProductReapproAlgorithm
  effectivePeriode: ReaproPeriode
  effectiveCoverageRate: number
  effectiveCoefSecu: number
  effectiveCoefWeekend: number
  resolvedStockMinimum: number | null
  /** True when algorithm was forced to STOCK_MINIMUM because productionCoveringRate is absent */
  forcedStockMinimum: boolean
  besoin: number
  stockCible: number | null
  stockActuel: number
  stockDLCCourte: number
  stockArrivant: number
  stockProjete: number
  vma: number
  vmj: number
  prevision: number
  currentStockMin: number
  daysBefore: number
  stopOrdering: boolean
  poidsJourJ: number
  poidsJourJ1: number
  /** Jours couverts par la livraison (calendrier de production) — pour le gating week-end client. Stable vs taux. */
  coveringDays: DayOfWeek[]
  /** Somme des poids des jours couverts au-delà du jour de livraison (VMJ). Stable vs taux. */
  extraCoverageWeights: number
  /** Unités par colis (arrondi du besoin). */
  colisage: number
  /** Multiplicateur de besoin daté appliqué par le serveur pour la date de livraison de ce preview (1 = aucun). */
  needAdjustmentMultiplier: number
  /**
   * VMA/VJM RÉSOLUS par le serveur (incl. moyenne inter-boutiques × quantityFactor
   * pour les boutiques neuves), par période. Source autoritative pour le recompute
   * client du besoin → parité exacte, toutes boutiques.
   */
  salesIndicators: {
    vma3j: number
    vma5j: number
    vma7j: number
    vjm3j: number
    vjm5j: number
    vjm7j: number
  }
  isDisabled: boolean
}

// Brand data
export interface Brand extends TimestampsAndID {
  name: string
  image?: AttachmentFile // Optional image
}

export const MENU_TYPES = ['PLAT', 'DESSERT', 'BOISSON'] as const

export type MenuItemType = (typeof MENU_TYPES)[number]

export const MENU_TYPES_OPTIONS = [
  { label: 'Aucun' as const, value: null },
  ...MENU_TYPES.map((type) => ({
    label: type,
    value: type,
  })),
]

export const NEGOCE_TYPES = ['NEGOCE INTERNE', 'NEGOCE EXTERNE', 'SUSHI'] as const

export type NegoceType = (typeof NEGOCE_TYPES)[number]

const NEGOCE_OPTIONS_OBJ: { [K in NegoceType]: string } = {
  'NEGOCE EXTERNE': 'Négoce externe',
  'NEGOCE INTERNE': 'Négoce interne',
  SUSHI: 'Sushi',
}

export const NEGOCE_TYPES_OPTIONS: { value: NegoceType; label: string }[] = Object.entries(
  NEGOCE_OPTIONS_OBJ,
).map(([value, label]) => ({ value: value as NegoceType, label }))

// Product data returned by the Index query
export interface Product extends TimestampsAndID {
  // Product label
  reference: string
  dun14: string | null
  label: string

  // Product weight and price
  weight: number // grams
  price: number // euros
  weightPrice: number // centimes per gram
  sellPrice: number // euros
  sellWeightPrice: number // centimes per gram

  // Product description
  description: string
  nutriscore: 'A' | 'B' | 'C' | 'D' | 'E' | null
  conservation: string
  preparationMode: string | null
  allergens: string | null
  ingredients: string
  antiGaspi: number // Number before DLC to warn the user
  portions: string | null

  // Metadata
  isNew: boolean
  isActive: boolean
  colisage: number
  colisWeight: number // grams

  // Additional data from other tables
  brandId: number
  brand: Brand

  menuType: MenuItemType | null
  menuExtraPrice: number | null
  copilotCode: string | null
  negoceType: NegoceType

  categories: Category[]
  internalCategoryId: number
  internalCategory: InternalCategory
  reapproCategory: ReapproCategory
  reapproCategoryId: number

  nutritionalValueId: number
  nutritionalValues?: NutritionalValues

  optimizedImageUrls: string[]
  optimizedPreviewImageUrl: string | null

  // Attachments
  images?: ProductImage[]
  preparationVideo?: AttachmentFile // This is a video
  previewImage?: AttachmentFile // This is an image 9x16 vignette

  agPromo: number // ingeter : 70 for 70%
  agPrice: number
  taxRate: number // TVA rate for this product (5.5, 10, 20)
  dlcDays: number
  franco: number
  coldStartStockMin: number
  stopOrdering: boolean
  productionCoveringRate: number | null

  establishment?: Establishment
}

// Product data returned by the Show query (everything is loaded)
export interface LoadedProduct extends Product {
  // Attachments
  images: ProductImage[]
  nutritionalValues: NutritionalValues
}

export interface StoreProductMinimumStock extends TimestampsAndID {
  storeReference: string
  productReference: string
  weekEndMinimumStock: number | null
  mondayMinimumStock: number | null
  tuesdayMinimumStock: number | null
  wednesdayMinimumStock: number | null
  thursdayMinimumStock: number | null
  fridayMinimumStock: number | null
  label?: string // From the join with the store
}

export interface CampaignInfos {
  label: string
  reductionType: string
  reductionValue: number
}

export interface PromoProduct {
  product: Product
  campaignInfos?: CampaignInfos
  isAntiGaspi?: boolean
}

export interface NutritionalValues extends TimestampsAndID {
  energyValue: string // kcal
  fats: string // micrograms (high precision with integer)
  proteins: string // micrograms (high precision with integer)
  carbohydrates: string // micrograms (high precision with integer)
  sugars: string // micrograms (high precision with integer)
  polyols: string // micrograms (high precision with integer)
  fibers: string // micrograms (high precision with integer)
  sodium: string // micrograms (high precision with integer)
  salt: string // micrograms (high precision with integer)
  saturatedFats: string // micrograms (high precision with integer)
}

export interface ProductImage extends TimestampsAndID {
  productId: number
  image: AttachmentFile
}

export const CAMPAIGN_TARGETS = ['Produit', 'Utilisateur', 'Menu', 'Panier'] as const
export type CampaignTarget = (typeof CAMPAIGN_TARGETS)[number]
export const CAMPAIGN_TYPES = ['Taux', 'BRI', 'ONE_FOR_ONE'] as const
export type CampaignType = (typeof CAMPAIGN_TYPES)[number]

// Base campaign, with no target relation preloaded (used for the index query, that returns everything)
export interface PromoCampaignBase extends TimestampsAndID {
  type: CampaignTarget
  name: string
  isActive: boolean

  isMain: boolean
  alreadyUsed: boolean

  reductionType: CampaignType
  reductionValue: number

  startDate: string
  endDate: string
  isPublic: boolean
  publicCode: string | null
  isUsingUniqueCodes: boolean
}

export interface PromoUniqueUseCode extends TimestampsAndID {
  promoCampaignId: number
  code: string
  isUsed: boolean
}

export interface UserPromoCampaign extends PromoCampaignBase {
  type: 'Utilisateur'
  userTargets: User[]
  storeTargets: Store[]
}

export interface ProductPromoCampaign extends PromoCampaignBase {
  type: 'Produit'
  productTargets: Product[]
  storeTargets: Store[]
}

export interface MenuPromoCampaign extends PromoCampaignBase {
  type: 'Menu'
  menuTargets: string[]
  storeTargets: Store[]
}

export interface UserPromoCode extends TimestampsAndID {
  promoCampaign: PromoCampaignBase
  numberOfUses: number
  isMain: boolean
  alreadyUsed: boolean
}

// Campaign with loaded target (used in the show query, that returns only a specific campaign)
export type PromoCampaign = UserPromoCampaign | ProductPromoCampaign | MenuPromoCampaign

/** Helper : get a list of target IDs */
export const getTargetIds = (campaign: PromoCampaign): (number | string)[] => {
  if (campaign.type === 'Produit') {
    return campaign.productTargets.map((product) => product.id)
  }
  if (campaign.type === 'Utilisateur') {
    return campaign.userTargets.map((user) => user.id)
  }
  if (campaign.type === 'Menu') {
    return campaign.menuTargets
  }
  return []
}

/** Helper : get a list of store references */
export const getStoreTargetRefs = (campaign: PromoCampaign): string[] => {
  if (campaign.storeTargets) {
    return campaign.storeTargets.map((store) => store.checkpointStoreRef)
  }
  return []
}

// Store data
export interface Address extends TimestampsAndID {
  address: string
  addressAdditionnal?: string
  postalCode: string
  city: string
  country: Country
}

export const STORE_TYPES = [
  'BOUTIQUE',
  'SHIPPING',
  'WAREHOUSE',
  'POUBELLE',
  'INTERNAL_STOCK',
  'REMOTE',
] as const

export type StoreType = (typeof STORE_TYPES)[number]

export const STORE_STATUSES = [
  'PREPARING',
  'STOCKING',
  'OPEN',
  'TEMPORARILY_CLOSED',
  'PERMANENTLY_CLOSED',
] as const

export type StoreStatus = (typeof STORE_STATUSES)[number]

export const MANUALLY_ASSIGNABLE_STORE_STATUSES = [
  'PREPARING',
  'STOCKING',
  'OPEN',
  'TEMPORARILY_CLOSED',
] as const satisfies readonly StoreStatus[]

/** Status capabilities are intentionally centralized: `stores.is_online` is legacy-only. */
export const CUSTOMER_VISIBLE_STORE_STATUSES = ['OPEN'] as const satisfies readonly StoreStatus[]
export const REAPPRO_ENABLED_STORE_STATUSES = [
  'STOCKING',
  'OPEN',
] as const satisfies readonly StoreStatus[]
export const ACTIVE_ANALYTICS_STORE_STATUSES = ['OPEN'] as const satisfies readonly StoreStatus[]
export const BEARCOD_CATALOG_STORE_STATUSES = [
  'PREPARING',
] as const satisfies readonly StoreStatus[]
export const BEARCOD_REAL_DATA_STORE_STATUSES = [
  'STOCKING',
  'OPEN',
  'TEMPORARILY_CLOSED',
] as const satisfies readonly StoreStatus[]

export const CAMERA_TYPES = ['JVC', 'UNIFI'] as const
export type CameraType = (typeof CAMERA_TYPES)[number]

export const COUNTRIES = ['FRANCE'] as const

export type Country = (typeof COUNTRIES)[number]

export interface StoreTagDTO {
  id: number
  label: string
}

export interface Store extends TimestampsAndID {
  checkpointStoreRef: string
  label: string
  shortLabel: string | null
  addressId: number | null
  address: Address | null
  storeType: StoreType
  cameraType: CameraType | null
  isOnline: boolean
  status: StoreStatus
  lunaStoreRef: string | null
  bearcodName: string | null
  legalEntityId: number
  siret: string | null
  openingDate: string
  quantityFactor: number
  growthFactor: number
  /** Présent quand la relation est préchargée (index/show stores). */
  tags?: StoreTagDTO[]
}

export interface Kiosk extends TimestampsAndID {
  label: string
  cpiId: string
  store: Store
  supportMode: boolean
  cartId: number
  transaction: string
  cart?: Cart
}

export const OPENING_SESSION_DISPLAY_STATUSES = [
  'awaitingApproval',
  'awaitingVerification',
  'awaitingOpening',
  'awaitingReport',
  'validated',
  'retained',
  'releasing',
  'released',
  'refused',
  'abandoned',
] as const
export type OpeningSessionDisplayStatus = (typeof OPENING_SESSION_DISPLAY_STATUSES)[number]

export const PRE_AUTHORIZATION_DURATION_SECONDS = 600

export const PRE_AUTHORIZATION_MAX_CHARGE_CENTS = 4900

export interface OpeningSession extends TimestampsAndID {
  kioskId: number
  storeId: number
  method: OpeningEventMethod
  status: OpeningSessionStatus
  valid: boolean | null
  granted: boolean | null
  openingGranted: boolean | null
  contactless: boolean | null
  preAuthorizationId: string | null
  preAuthorizationAmount: number | null
  preAuthorizationDuration: number | null
  preAuthorizationClosingAmount: number | null
  preAuthorizationReceipt: string | null
  preAuthorizationError: string | null
  cardNumber: string | null
  cardType: string | null
  cardBrand: string | null
  cartId: number | null
  error: string | null
  startedAt: string
  closedAt: string | null
  retainedAt: string | null
  releasedAt: string | null
  releaseRequestedAt: string | null
  openingId: string | null
  kiosk?: Kiosk
  store?: Store
  cart?: { id: number; status: CartStatus; amountTotal: number; validatedAt: string | null }
}

export interface PaginatedOpeningSessions {
  data: OpeningSession[]
  meta: { total: number; perPage: number; currentPage: number; lastPage: number }
}

export const getOpeningSessionDisplayStatus = (
  session: Pick<
    OpeningSession,
    | 'status'
    | 'granted'
    | 'valid'
    | 'openingGranted'
    | 'startedAt'
    | 'retainedAt'
    | 'releasedAt'
    | 'releaseRequestedAt'
    | 'preAuthorizationDuration'
  >,
): OpeningSessionDisplayStatus => {
  if (session.status === 'pending') {
    if (session.granted === null) return 'awaitingApproval'
    if (session.valid === null) return 'awaitingVerification'
    if (session.openingGranted === null) return 'awaitingOpening'
    return 'awaitingReport'
  }
  if (session.status === 'timedout') return 'abandoned'
  if (session.status === 'cancelled') return 'abandoned'
  if (session.status === 'failed') return 'abandoned'
  if (session.granted === false || session.openingGranted === false) return 'refused'
  if (session.retainedAt !== null) return 'retained'
  if (session.releasedAt !== null) return 'released'
  if (session.releaseRequestedAt !== null) return 'releasing'
  const durationSeconds = session.preAuthorizationDuration ?? PRE_AUTHORIZATION_DURATION_SECONDS
  if (new Date(session.startedAt).getTime() < Date.now() - durationSeconds * 1000) return 'released'
  return 'validated'
}

export const OPENING_SESSION_RECORDING_STATUSES = [
  'recording',
  'pending',
  'exporting',
  'ready',
  'failed',
] as const
export type OpeningSessionRecordingStatus = (typeof OPENING_SESSION_RECORDING_STATUSES)[number]

export const RECORDING_EXPORT_PROVIDERS = ['jvc', 'unifi'] as const
export type RecordingExportProvider = (typeof RECORDING_EXPORT_PROVIDERS)[number]

export const PENDING_RECORDING_EXPORT_STATUSES = [
  'pending',
  'exporting',
  'done',
  'skipped',
  'failed',
] as const
export type PendingRecordingExportStatus = (typeof PENDING_RECORDING_EXPORT_STATUSES)[number]

export interface RecordingExportCamera {
  cameraRef: string
  cameraName: string
  cameraLabel: string | null
}

export interface OpeningSessionRecording extends TimestampsAndID {
  openingSessionId: number
  cameraName: string
  cameraLabel: string | null
  status: OpeningSessionRecordingStatus
  startedAt: string
  endedAt: string | null
  playbackUrl: string | null
  provider: RecordingExportProvider | null
  cameraRef: string | null
  providerExportId: string | null
  attempts: number
  error: string | null
}

// CPI OPENING EVENTS
export * from './schemas/openingEvents.js'
export * from './types/openingEvents.types.js'
export * from './types/supply.types.js'

export const UNIQUE_ARTICLE_SOLD_STATUSES = [
  'NORMAL',
  'ANTI_GASPI',
  'MENU',
  'PROMO',
  'PROMO_MENU',
  'STUDENT_MENU',
  'FREE',
  'WORKS_CONCIL',
  'STUDENT',
  'OTHER',
  'BRI',
] as const

export type UniqueArticleSoldStatus = (typeof UNIQUE_ARTICLE_SOLD_STATUSES)[number]

export const CART_ITEM_TYPES = ['ARTICLE', 'MENU'] as const

export type CartItemType = (typeof CART_ITEM_TYPES)[number]

export const UNIQUE_ARTICLE_PAID_WAY = ['APP', 'CPI'] as const

export type UniqueArticlePaidWay = (typeof UNIQUE_ARTICLE_PAID_WAY)[number]

export const UNIQUE_ARTICLE_SHOPPING_WAY = ['IN_STORE', 'CLICK_AND_COLLECT'] as const

export type UniqueArticleShoppingWay = (typeof UNIQUE_ARTICLE_SHOPPING_WAY)[number]
export interface UniqueArticle extends TimestampsAndID {
  checkpointArticleRef: string
  productReference: string
  storeReference: string
  weight: number // Grams
  dlc: string // ISO date string

  displayedDlc: string // Computed field, formatted like 27/02/2024
  isAntiGaspi: boolean // Computed field
  isExpired: boolean // Computed field
  isConfirmed: boolean // Computed field

  product: Product // Preloaded by default
  store: Store // not preloaded by default

  state: UniqueArticleState

  soldPrice: number | null
  soldStatus: UniqueArticleSoldStatus | null
  paidWay: UniqueArticlePaidWay | null
  shoppingWay: UniqueArticleShoppingWay | null
  buyingPrice: number | null

  warehouseAt: string | null // ISO date string
  shippingAt: string | null // ISO date string
  boutiqueAt: string | null // ISO date string
  lostAt: string | null // ISO date string
}

export interface WorksConcilPreview extends TimestampsAndID {
  label: string
  isActive: boolean
  maxMonthAmountPerEmployee: number
  gozokiPercentage: number
  worksConcilPercentage: number
  numberOfEmployees: number
  totalUsedAmount: number
  numberOfActivesEmployees: number
  startDate: string // ISO date string
  endDate: string // ISO date string
}

export interface WorksConcil extends TimestampsAndID {
  label: string
  isActive: boolean
  isOnDate: boolean

  companyLabel: string
  companyCity: string
  companyAddress: string
  companySiren: string

  maxMonthAmountPerEmployee: number
  gozokiPercentage: number
  worksConcilPercentage: number

  startDate: string // ISO date string
  endDate: string // ISO date string

  emails: string[]
  employees: User[]
}

export const CART_STATUSES = ['pending', 'validated', 'cancelled', 'stolen'] as const

export type CartStatus = (typeof CART_STATUSES)[number]

export interface Cart extends TimestampsAndID {
  userId: number | null
  status: CartStatus
  user?: User // Not preloaded by default !
  report: CartReportWithTaxes
  articles: UniqueArticle[] // Preloaded by default
  buyingPrice: number | null
  amountTotal?: number
  store: Store // Preloaded by default
}

export interface CartsWithReportUrl extends Cart {
  reportUrl: string
}

/**
 * Lightweight cart summary used in the customer info modal opened from the
 * "Paniers" page in the BO. Only fields actually displayed in the history
 * table are included; full Cart payload is not needed.
 */
export interface CartHistoryEntry {
  id: number
  storeId: number
  validatedAt: string | null
  amountTotal: number
  itemCount: number
}

/**
 * Discriminated payload returned by `GET /carts/:id/customer-info`.
 * - `anonymous`: cart paid with a bare credit card (no user account).
 * - `user`: cart linked to a registered user; includes profile + last 20
 *   validated carts of that user.
 */
export type CartCustomerInfo =
  | { kind: 'anonymous' }
  | { kind: 'user'; user: User; history: CartHistoryEntry[] }

export interface CartReport {
  items: FinalCartUnit[]
  subTotal: number
  otherPromotions: CartPromotion[]
  total: number
}

export interface CartPromotion {
  type: PromoType
  label: string
  amount: number
}

export interface CartTax {
  rate: number
  amount: number
}

interface ItemBase {
  label: string
  unitPrice: number
  quantity: number
  amount: number
}

export interface ProductItem extends ItemBase {
  type: 'ARTICLE'
  productReference: string
  article: UniqueArticle
}

const PROMO_TYPES = ['ANTI_GASPI', 'ONE_FOR_ONE', 'WORKS_CONCIL', 'BRI', 'STUDENT'] as const

export type PromoType = (typeof PROMO_TYPES)[number]

export interface CartReportWithTaxes extends CartReport {
  totalExTax: number
  taxes: CartTax[]
}
export interface CartUnit {
  item: ProductItem
  promotions: CartPromotion[]
}

export interface FinalCartUnit {
  label: string
  unitPrice: number
  quantity: number
  amount: number
  type: 'ARTICLE' | 'MENU'
  content: { label: string }[]
  imageUrl: string | null
  promotions: CartPromotion[]
  taxRate: number
}
// Stock data
export interface StockMetadata {
  articleCount: number
  antiGaspiArticleCount: number
  expiredArticleCount: number
  totalStockValue: number
  totalAntiGaspiStockValue: number
  totalExpiredStockValue: number
}

export interface StockEntry {
  id: number
  imageUrl: string | null
  reference: string
  label: string
  colisage: number
  weight: number | null

  buyingPrice: number

  stock: number
  agStock: number
  expiredStock: number
  expiredOrWillExpireStock: number
  vma: number
  vjm: number
  vma3j: number
  vma5j: number
  vma7j: number
  vjm3j: number
  vjm5j: number
  vjm7j: number

  dailyMinStocksMap: { [key: string]: number }
  dun14: string

  totalValue: number
  totalValueAntiGaspi: number
  totalValueExpired: number
  antiGaspiValue: number

  currentReaproNeeds: number
  warehouseIn: string

  establishmentId: number
  sellerId: number
  internalCategory: InternalCategory
}

export interface StockData {
  metadata: StockMetadata
  rows: StockEntry[]
}

export interface QrCode {
  token: string
  image?: string
}

export interface PayzenPaymentToken {
  paymentMethodToken: string
  paymentMethodType: 'CARD' | 'SDD'
  creationDate: string
  cancelationDate: string | null
  status: 'ACTIVE' | 'CANCELLED' | 'EXPIRED' | 'ALL'
  tokenDetails: {
    expiryMonth: number
    expiryYear: number
    effectiveBrand: string
    pan: string
  }
}

export interface PaymentResponse {
  _type: string
  customer: {
    email: string
    reference: string | null
  }
  orderDetails: {
    _type: string
    orderId: string
    orderamountTotal: number
  }
  serverDate: string
  orderStatus: string
  transactions: {
    paymentMethodToken: string
    paymentMethodType: string
    shopId: string
    status: string
    uuid: string
  }[]
}
export const UNIQUE_ARTICLE_STATES = [
  'SOLD',
  'IN_STOCK',
  'FAULTY', // Not used anymore
  'STOLEN',
  'DESTROYED',
  'IN_CART',
  'DELETED', // use as a soft delete to keep the history
  'DEFECTIVE_PACKAGING',
  'INVENTORY_DELETED',
  'LOST_IN_TRANSPORT',
] as const

export type UniqueArticleState = (typeof UNIQUE_ARTICLE_STATES)[number]

export const DEFAULT_UNIQUE_ARTICLE_STATE: UniqueArticleState = 'IN_STOCK'
export interface DailyDashboardKpi {
  id: number
  storeId: number
  date: string
  ca: number
  caHt: number
  caAntiGaspi: number
  caAntiGaspiHt: number
  buyingPrice: number
  oneForOneOfferedBuyingPrice: number
  productsSoldCount: number
  antiGaspiProductsSold: number
  cartsCount: number
  productsSoldMap: { [key: string]: { ref: string; count: number } }
  productsSoldMapByCa: { [key: string]: { ref: string; ca: number } }
  categoriesSoldMap: { [key: number]: { id: number; count: number } }
  categoriesSoldMapByCa: { [key: number]: { id: number; ca: number } }
  appUsersCount: number
  appNewUsersCount: number
  appDeletedUsersCount: number
  appActivatedUsersCount: number
  appCartsCount: number
  appUsersList: number[]
  studentAmount: number
  studentCartsCount: number
  studentCartsCa: number
  studentUsersCount: number
  wcCa: number
  wcCartsCount: number
  wcGozokiAmount: number
  wcCompanyAmount: number
}

export interface DailyStockKpi {
  id: number
  storeId: number
  date: string
  articlesSoldMap: { [key: number]: { ref: string; count: number } }
  articlesStolenMap: { [key: number]: { ref: string; count: number } }
  articlesFaultyMap: { [key: number]: { ref: string; count: number } }
  articlesDestroyedMap: { [key: number]: { ref: string; count: number } }
  articlesExpiredMap: { [key: number]: { ref: string; count: number } }
  articlesDefectiveMap: { [key: number]: { ref: string; count: number } }
  articlesSoldCount: number
  articlesFaultyCount: number
  articlesDestroyedCount: number
  articlesStolenCount: number
  articlesExpiredCount: number
  articlesDefectiveCount: number
}

export interface AccountingKpis {
  date: string
  main: {
    internalNegoceHt: number
    internalNegoceTva: number
    externalNegoceHt: number
    externalNegoceTva: number
    sushisTtc: number
    amountCb: number
    amountConecs: number
    unPaidAmount: number
    promoAmountHt: number
    promoAmountTva: number
  }
}

export interface HoursData {
  time: number
  count: number
  amount: number
}

export interface TimeSpentData {
  time: number
  averageTimeInShop: number
  averageTimeInDesk: number
}

export interface Lot {
  lot: string
  ean: string
  nb: number
  label: string
  dlc: string
  tracking: string
  blocked: boolean
  comment: string
  blockedAt: string | null
  blockedBy: string | null
  unBlockedAt: string | null
  articles: string[]
}

export interface GlobalParams {
  menuPlatBoissonName: string
  menuPlatBoissonPrice: number
  menuPlatBoissonDessertName: string
  menuPlatBoissonDessertPrice: number
}

export interface StoreStock {
  storeReference: string
  stock: number
  label: string
}

export interface NotificationPayload {
  path: string | null | undefined
}

export interface AnomalyAPIResponse {
  id: string
  anomalyTypeId: string
  link: string | null
  timestamp: string
  // temporaryLink: string;
  videoId: string
  startTime: string
}

export interface CameraStore {
  id: string
  name: string
}

export interface AiArticle {
  productLabel: string
  productImageUrl: string | null
  productQuantity: number
}

export interface VideoIdsResponse {
  video_id: number[]
}

export interface Anomaly {
  name: string
  link: string | null
  temporaryLink: string
  id: string
  anomalyTypeId: string
  timestamp: string
  color: string
}

export const KIOSK_TYPES = ['CHECK_IN', 'CHECK_OUT'] as const
export type KioskType = (typeof KIOSK_TYPES)[number]

export const INVENTORY_TYPES = ['CLASSIC', 'TRANSPORT'] as const
export type InventoryType = (typeof INVENTORY_TYPES)[number]

export const INVENTORY_STATUS = ['pending', 'validating', 'validated', 'canceled'] as const
export type InventoryStatus = (typeof INVENTORY_STATUS)[number]

export const INVENTORY_LOG_ACTIONS = [
  'create',
  'add-articles',
  'finish',
  'unfinish',
  'validate',
  'revalidate',
  'cancel',
] as const
export type InventoryLogAction = (typeof INVENTORY_LOG_ACTIONS)[number]

export const CART_ARTICLE_LOG_EVENTS = [
  'added-to-cart',
  'removed-from-cart',
  'not-found',
  'expired',
  'blocked',
  'bad-state',
  'created-from-epc-decode',
  'created-from-sold-duplicate',
  'sanitized-inventory-deleted',
  'sanitized-lost-in-transport',
  'store-mismatch',
  'sold',
  'stolen',
  'cancelled',
  'epc-decode-failed',
  'epc-bad-format',
  'ean13-mismatch',
] as const
export type CartArticleLogEvent = (typeof CART_ARTICLE_LOG_EVENTS)[number]

export interface UnscannableProduct {
  productReference: string
  count: number
}

export interface InventorySummary extends TimestampsAndID {
  label: string
  comment: string
  storeReference: number
  unScannableProducts?: { unScannableProducts: UnscannableProduct[] }
  status: InventoryStatus
  inventoryType: InventoryType
  isFinished: boolean
}

export interface InventoryDetail extends InventorySummary {
  incomingStock: UniqueArticle[]
  currentStock: UniqueArticle[]
  deliveredArticles: UniqueArticle[]
  lostArticles: UniqueArticle[]
}

// Per-product aggregation row used by store inventory stats and dashboards.
export interface ProductWithCount extends Product {
  count: number
  ca: number
}

// Per-inventory counts already classified into the same buckets as the global
// aggregation. Lets the BO render a per-inventory row inline without
// re-fetching the inventory and re-classifying its articles.
export interface InventoryCounts {
  currentStock: number
  incomingStock: number
  delivered: number
  sold: number
  destroyed: number
  defective: number
  stolen: number
  errored: number
}

// Lightweight inventory header used in the aggregated stats payload (no preloads).
export interface InventoryHeader {
  id: number
  label: string
  createdAt: string
  validatedAt: string | null
  isFinished: boolean
  status: InventoryStatus
  counts: InventoryCounts
}

// Per-bucket result. `totalCa` is only meaningful on sold/destroyed/defective/stolen
// (used in the dashboard percentages); delivered/errored leave it omitted.
export interface InventoryAggregateBucket {
  products: ProductWithCount[]
  totalCount: number
  totalCa: number
}

export interface InventoriesAggregatedStats {
  inventories: InventoryHeader[]
  buckets: {
    sold: InventoryAggregateBucket
    delivered: InventoryAggregateBucket
    destroyed: InventoryAggregateBucket
    defective: InventoryAggregateBucket
    stolen: InventoryAggregateBucket
    errored: InventoryAggregateBucket
  }
}

export interface MinStockValues extends TimestampsAndID {
  weekEndMinimumStock?: number
  mondayMinimumStock?: number
  tuesdayMinimumStock?: number
  wednesdayMinimumStock?: number
  thursdayMinimumStock?: number
  fridayMinimumStock?: number
}

export const DAYS_OF_WEEK = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number]

export const frenchDaysOfWeek: Record<DayOfWeek, string> = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche',
}

export interface DailyMinsAndSold {
  [key: string]: {
    minMap: { [key: string]: number | undefined }

    soldMap: { [key: string]: { totalCount: number; totalDays: number; history: number[] } }
  }
}

export interface Seller extends TimestampsAndID {
  reference?: string
  label: string
  address?: Address
  establishments?: Establishment[]
}

export interface Establishment extends TimestampsAndID {
  reference?: string
  label: string
  address?: Address
  seller?: Seller
}

export const OPENING_MODE_VALUES = [
  'UNATTENDED',
  'ATTENDED',
  'CLOSED',
  'UNATTENDED_AUTHENTICATED',
  'ATTENDED_PRE_AUTHORIZATION',
  'FREE_ENTRY',
] as const
export type OpeningMode = (typeof OPENING_MODE_VALUES)[number]

export const frenchOpeningMode: Record<OpeningMode, string> = {
  UNATTENDED: 'Ouverture classique',
  ATTENDED: 'Ouverture manuelle',
  CLOSED: 'Fermeture complète',
  UNATTENDED_AUTHENTICATED: 'Ouverture avec compte Lucie',
  ATTENDED_PRE_AUTHORIZATION: 'Ouverture avec pré-autorisation',
  FREE_ENTRY: 'Toujours ouvert',
}

export interface OpeningConfigLine {
  openingMode: OpeningMode
  beginTime: string
  endTime: string
  preAuthorizationAmount?: number
}

export interface OpeningConfigLineString {
  openingMode: OpeningMode
  beginTime: string
  endTime: string
  preAuthorizationAmount?: number
}

export interface OpeningConfigDBLine {
  openingMode: OpeningMode
  beginTime: string // hh:mm
  preAuthorizationAmount?: number
}

export type OpeningConfigDB = Record<number, OpeningConfigDBLine[]>

export interface OpeningConfigValidationResponse {
  status: 'success' | 'error'
  message: string
}

export interface PendingStoreKiosk {
  kioskId: number
  storeId: number
  storeLabel?: string
}

export interface VisioLink {
  url: string
  message: string
  token: string
}

export interface VisioReview extends TimestampsAndID {
  visioRoomId: number
  transcript: string
  grade: number | null
  noGradeReason: NoGradeReason | null
  gradeExplanation: string | null
  durationSeconds: number | null
  managerGrade: number | null
  managerComment: string | null
  managerId: number | null
  managerReviewedAt: string | null
  aiGradeValidatedBy: number | null
  aiGradeValidatedAt: string | null
}

// -- Visio Reviews Dashboard Types -- //

export interface VisioReviewInteraction {
  id: number
  visioRoomId: number
  grade: number | null
  gradeExplanation: string | null
  noGradeReason: NoGradeReason | null
  managerGrade: number | null
  managerComment: string | null
  managerId: number | null
  manager: { firstName: string; lastName: string } | null
  managerReviewedAt: string | null
  durationSeconds: number | null
  resolutionTimestampSeconds: number | null
  aiGradeValidatedBy: number | null
  aiGradeValidator: { firstName: string; lastName: string } | null
  aiGradeValidatedAt: string | null
  resolutionStatus: ResolutionStatus | null
  interactionAt: string
  operator: { id: number; firstName: string; lastName: string }
  store: { id: number; label: string }
  kiosk: { id: number; type: string }
}

export type VisioReviewInteractionsSortBy = 'time' | 'rating' | 'duration'
export type VisioReviewSortDir = 'asc' | 'desc'

export interface VisioReviewInteractionsResponse {
  rows: VisioReviewInteraction[]
  hasMorePage: boolean
  /** Only present on page 1 (i.e. when the request omits `page` or sets it to 1). */
  totalCount?: number
}

/**
 * `no-previous-data` / `no-current-data` ⇒ one of the windows has no rated
 * visios; trend is not computable. Companion `trendValue` is null in those
 * states.
 */
export type VisioReviewTrend = 'up' | 'down' | 'stable' | 'no-previous-data' | 'no-current-data'

export interface VisioReviewOperatorStats {
  adminId: number
  firstName: string
  lastName: string
  avgGrade: number | null
  visioCount: number
  avgDurationSeconds: number | null
  /** 0–1, or null when no row in the window had a `resolution_status`. */
  resolutionRate: number | null
  trend: VisioReviewTrend
  trendValue: number | null
}

export type VisioReviewOperatorsSortBy = 'rating' | 'visios' | 'duration' | 'resolution' | 'name'

export type VisioReviewGradeBucket =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | 'Sans note'

export interface VisioReviewOperatorRanked {
  adminId: number
  firstName: string
  lastName: string
}

export interface VisioReviewOperatorsCharts {
  byAvgGrade: (VisioReviewOperatorRanked & { avgGrade: number })[]
  topByVolume: (VisioReviewOperatorRanked & { visioCount: number })[]
}

export interface VisioReviewOperatorsResponse {
  rows: VisioReviewOperatorStats[]
  hasMorePage: boolean
  /** Only present on page 1 (i.e. when the request omits `page` or sets it to 1). */
  totalCount?: number
}

export interface VisioReviewOperatorDetail {
  kpis: {
    avgGrade: number | null
    totalVisios: number
    avgDurationSeconds: number | null
    /** Sum of `duration_seconds` across visios in the window, or null when none. */
    totalDurationSeconds: number | null
    /** 0–1, or null when no row in the window had a `resolution_status`. */
    resolutionRate: number | null
    /** totalVisios / nb de jours avec ≥1 visio, ou null si aucun jour travaillé. */
    avgVisiosPerWorkedDay: number | null
    /** totalDurationSeconds / nb de jours travaillés, ou null si aucun jour travaillé. */
    avgDurationPerWorkedDaySeconds: number | null
  }
  charts: {
    ratingDistribution: { range: VisioReviewGradeBucket; count: number }[]
    /** Empty when the date range is unbounded (Toujours). */
    trendChart: { label: string; avgGrade: number }[]
  }
}

export interface VisioReviewOperatorInteractionsResponse {
  rows: VisioReviewInteraction[]
  hasMorePage: boolean
}

export interface VisioReviewStoreStats {
  storeId: number
  storeName: string
  /** Total visios in the current window — graded or not. */
  interactionCount: number
  /** Null when no visios in the window were graded. */
  avgGrade: number | null
  avgDurationSeconds: number | null
  /** 0–1, or null when no row in the window had a `resolution_status`. */
  resolutionRate: number | null
  trend: VisioReviewTrend
  trendValue: number | null
}

export type VisioReviewStoresSortBy = 'rating' | 'interactions' | 'name' | 'duration' | 'resolution'

export interface VisioReviewStoresResponse {
  rows: VisioReviewStoreStats[]
  hasMorePage: boolean
  /** Only present on page 1 (i.e. when the request omits `page` or sets it to 1). */
  totalCount?: number
}

export interface VisioReviewStoresCharts {
  byAvgGrade: { storeId: number; storeName: string; avgGrade: number }[]
  byVolume: { storeId: number; storeName: string; interactionCount: number }[]
}

export interface VisioReviewStoreDetail {
  kpis: {
    avgGrade: number | null
    totalVisios: number
    avgDurationSeconds: number | null
    /** Sum of `duration_seconds` across visios in the window, or null when none. */
    totalDurationSeconds: number | null
    /** 0–1, or null when no row in the window had a `resolution_status`. */
    resolutionRate: number | null
    /**
     * visio interactions / validated carts on the period. 0 when both sides
     * are 0; 1 when there are visios but no validated cart; otherwise the raw
     * ratio (may exceed 1.0). Numerator ignores the `onlyVerified` toggle.
     */
    assistanceRate: number
  }
  charts: {
    ratingDistribution: { range: VisioReviewGradeBucket; count: number }[]
    /** Empty when the date range is unbounded (Toujours). */
    trendChart: { label: string; avgGrade: number }[]
  }
}

export interface VisioReviewStoreOperator {
  adminId: number
  firstName: string
  lastName: string
  visioCount: number
  avgGrade: number | null
}

export interface VisioReviewStoreOperatorsResponse {
  rows: VisioReviewStoreOperator[]
  hasMorePage: boolean
}

export interface VisioReviewStoreInteractionsResponse {
  rows: VisioReviewInteraction[]
  hasMorePage: boolean
}

export interface VisioReviewManagerEvaluation {
  managerGrade: number
  managerComment?: string
}

// -- Global tab charts -- //

export interface VisioReviewRatingBands {
  red: number
  amber: number
  green: number
  noGrade: number
}

export interface VisioReviewFunnel {
  totalVisios: number
  gradedVisios: number
  validatedVisios: number
  resolvedVisios: number
}

export interface VisioReviewTrendPoint {
  bucketStart: string
  avgGrade: number | null
  prevAvgGrade: number | null
}

export interface VisioReviewVolumeTimelinePoint {
  bucketStart: string
  count: number
  red: number
  amber: number
  green: number
  noGrade: number
}

export type VisioReviewBucketing = 'day' | 'week' | 'month'

export interface VisioReviewGlobalCharts {
  ratingBands: {
    current: VisioReviewRatingBands
    previous: VisioReviewRatingBands | null
  }
  funnel: VisioReviewFunnel
  trend: VisioReviewTrendPoint[]
  volumeTimeline: VisioReviewVolumeTimelinePoint[]
  ratingDistribution: { range: VisioReviewGradeBucket; count: number }[]
  bucketing: VisioReviewBucketing
}

// -- Activity heatmap (per operator / per store, used in detail dialogs) -- //

export interface VisioReviewActivityHeatmapCell {
  /** 0 = Monday, …, 6 = Sunday (Europe/Paris). */
  dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6
  /** 0..23 (Europe/Paris). */
  hour: number
  count: number
}

export interface VisioReviewActivityHeatmap {
  cells: VisioReviewActivityHeatmapCell[]
  max: number
  /**
   * Length 7, indexed Mon=0..Sun=6 (Europe/Paris). Number of distinct
   * calendar days with ≥1 visio for each weekday in the queried window.
   * Frontend uses this as the per-weekday divisor to compute averages.
   */
  workedDaysPerWeekday: number[]
}

export const FRIDGE_TYPES = {
  FRIDGE: 'FRIDGE',
  COLD_ROOM: 'COLD_ROOM',
} as const
export type FridgeType = (typeof FRIDGE_TYPES)[keyof typeof FRIDGE_TYPES]

export const CONTACT_TYPES = ['EMAIL', 'PHONE', 'WEBSITE', 'ADDRESS', 'SOCIAL'] as const
export type ContactType = (typeof CONTACT_TYPES)[number]

export interface JvcCamera {
  id: number
  label: string
  recordOnPreAuth?: boolean
}

export interface CameraStream extends TimestampsAndID {
  storeId: number
  domain: string
  username: string
  password: string
  cameras: JvcCamera[]
}

export interface SetStoreCameraTypeInput {
  storeId: number
  type: CameraType | null
}

export interface UpdateCameraStreamInput {
  storeId: number
  domain?: string
  username?: string
  password?: string
  cameras?: JvcCamera[]
}

export interface StoreCameraConfigSummary extends TimestampsAndID {
  storeId: number
  hasVpnConfig: boolean
  hasVpnCredentials: boolean
  runtime: StoreCameraRuntimeSummary | null
}

export interface StoreCameraRuntimeSummary {
  status: string
  hostname: string | null
  baseUrl: string | null
  lastReconcileRequestedAt: string | null
  lastReconcileStartedAt: string | null
  lastReconcileSucceededAt: string | null
  lastReconcileFailedAt: string | null
  lastErrorMessage: string | null
}

export interface StoreCameraConfigState {
  config: StoreCameraConfigSummary | null
  runtime: StoreCameraRuntimeSummary | null
}

export interface StoreCameraConfigUpsertInput {
  storeId: number
  vpnConfig?: string
  vpnCredentials?: string
}

export interface StoreCamera extends TimestampsAndID {
  storeId: number
  label: string
  name: string
  streamUrl: string
  // Ready-to-use playback URLs, computed server-side regardless of whether the
  // store is UNIFI or JVC. Append the viewer token (from /camera-view-session)
  // before using them. Null when not playable yet (e.g. runtime not ready).
  previewStreamUrl: string | null
  fullStreamUrl: string | null
  snapshotUrl: string | null
  recordOnPreAuth: boolean
}

export interface Go2RtcStreamProducer {
  url: string
}

export interface Go2RtcStream {
  producers?: Go2RtcStreamProducer[]
  consumers?: unknown[]
}

export type Go2RtcStreamMap = Record<string, Go2RtcStream>

export interface CreateStoreCameraInput {
  storeId: number
  label: string
  streamUrl: string
}

export interface UpdateStoreCameraInput {
  storeId: number
  cameraId: number
  label?: string
  streamUrl?: string
  recordOnPreAuth?: boolean
}

// ********************************************************* //
//                    REAL ESTATE                            //
// ********************************************************* //

export const STORE_PROJECT_STATUSES = [
  'BEFORE_PROJECT',
  'UNDER_STUDY',
  'ADVANCED_PROJECT',
  'SECURED',
  'WORKS',
  'OPENABLE',
  'OPEN',
] as const
export type StoreProjectStatus = (typeof STORE_PROJECT_STATUSES)[number]

export const TASK_STATUSES = ['TODO', 'IN_PROGRESS', 'DONE'] as const
export type TaskStatus = (typeof TASK_STATUSES)[number]

export const INSEE_DEMOGRAPHICS_YEARS = [2022] as const
export type InseeDemographicsYear = (typeof INSEE_DEMOGRAPHICS_YEARS)[number]

export const INSEE_DEMOGRAPHICS_SOURCES = ['INSEE'] as const
export type InseeDemographicsSource = (typeof INSEE_DEMOGRAPHICS_SOURCES)[number]

export interface InseeDemographics {
  inhabitants: number
  workers15p: number
}

export interface InseeFullDemographics {
  year: InseeDemographicsYear
  source: InseeDemographicsSource
  demographics: Record<number, InseeDemographics>
}

// Demographics zone types for store project analysis
export const DEMOGRAPHICS_ZONES = ['r1', 'r2', 'r3', 'r4'] as const
export type DemographicsZone = (typeof DEMOGRAPHICS_ZONES)[number]

export const DEMOGRAPHICS_RADIUS_MAP: Record<DemographicsZone, number> = {
  r1: 400, // 5 min walking
  r2: 800, // 10 min walking
  r3: 2000, // 5 min driving
  r4: 4000, // 10 min driving
}

export const DEMOGRAPHICS_ZONE_LABELS: Record<DemographicsZone, string> = {
  r1: '5 min à pied (~ 400m)',
  r2: '10 min à pied (~ 800m)',
  r3: '5 min en voiture (~ 2km)',
  r4: '10 min en voiture (~ 4km)',
}

// Store project contact
export interface StoreProjectContact {
  id: string
  name: string
  email?: string
  phone?: string
  contactRole?: ContactRole | null
  contactSubrole?: ContactSubrole | null
}

// Store project document
export interface StoreProjectDocument {
  url: string
  name: string
  size: number
  uploadedAt: string
}

// Polymorphic person reference (can be either an admin or a contact)
export interface PersonReference {
  adminId?: number | null
  contactId?: number | null
}

// ---- Sub-table form data types ----

export interface StoreProjectWorksFormData {
  workDurationWeeks: number | null
  keyReceptionDate: string | null
  workStartDate: string | null
  workEndDate: string | null
  openingDate: string | null
  moeExecution: PersonReference
  projectManagerAdminId: number | null
  fitter: PersonReference
  preliminaryVisitDate: string | null
  technicalAuditTodoDate: string | null
  technicalAuditPlannedDate: string | null
  technicalAuditDoneDate: string | null
  proDceTodoDate: string | null
  proDceStartedDate: string | null
  proDceDoneDate: string | null
  proDceTargetDate: string | null
  validatedGoBudget: number | null
  dgdBudget: number | null
}

export interface StoreProjectLeaseFormData {
  projectTypology: ProjectTypology | null
  implementationTypology: ImplementationTypology | null
  contractType: LeaseContractType | null
  totalFloorArea: number | null
  salesFloorArea: number | null
  backOfficeFloorArea: number | null
  glaSurface: number | null
  expectedSigningDate: string | null
  loiValidationDate: string | null
  promiseProjectedDate: string | null
  promiseSignatureDate: string | null
  signatureDate: string | null
  securisation: SecurisationType | null
  leaseStartDate: string | null
  coproApproval: CoproApprovalStatus | null
}

export interface StoreProjectDesignFormData {
  designRequestDate: string | null
  designStatus: DesignStatus | null
  fridgeCountX: number | null
  fridgeCountY: number | null
  fridgeCountZ: number | null
  checkoutCount: number | null
  checkinVersion: CheckinVersion | null
  validatedPlanUrl: string | null
  screen24Gourmandise: number | null
  screen26Gourmandise: number | null
  screen24Cafe: number | null
  screen26Cafe: number | null
  screen26Fridges: number | null
  externalScreens: number | null
}

export interface StoreProjectPermitsFormData {
  designMoe: PersonReference
  dpFilingDate: string | null
  dpObtainedDate: string | null
  atFilingDate: string | null
  atObtainedDate: string | null
  deFilingDate: string | null
  deObtainedDate: string | null
  launchClearance: LaunchClearanceStatus | null
}

// Store project form data type (for form management)
export interface StoreProjectFormData {
  name: string
  shortLabel: string | null
  devId: string | null
  dev: number | null
  address: string
  city: string
  postalCode: string
  surfaceArea: number | null
  score: number | null
  status: StoreProjectStatus
  isAbandoned: boolean
  rentPerYear: number | null
  works: number | null
  leaseFunds: number | null
  otherCosts: string | null
  storeName: string | null
  storeRef: string | null
  contacts: StoreProjectContact[]
  googleMapsUrl: string | null
  latitude: number | null
  longitude: number | null
  photoUrls: string[]
  documents: StoreProjectDocument[]
  comment: string
  worksData: StoreProjectWorksFormData | null
  leaseData: StoreProjectLeaseFormData | null
  designData: StoreProjectDesignFormData | null
  permitsData: StoreProjectPermitsFormData | null
}

export interface RealEstateKpis {
  avgLandCostEur: number | null
  avgLandCostPercentage: number | null
  /** Average rent per store (€) PRORATED over the period (annual rent × open days / 365), same perimeter as the land cost. */
  avgRentEur: number | null
  /** Total logistic cost (€) over the period: 0.60 €/kg of goods sold + stolen + destroyed. Period mode only. */
  logisticCostEur: number | null
  /** Average logistic cost per store (€) over the period, same perimeter. Period mode only. */
  logisticCostPerStoreEur: number | null
  avgWorksBudgetEur: number | null
  avgWorksBudgetPercentage: number | null
  /** Net change in open (non-abandoned) store count vs the comparison horizon (see KPIs request). */
  openStoresDelta: number | null
  /** Avg annual CA per store — rent ratio denominator (HT in period mode, TTC in the legacy map path). */
  averageAnnualCaEur: number | null
  /** Avg HT CA per store over the PERIOD, same store scope as the land cost — land-cost ratio denominator (period mode only). */
  landCostCaHtPerStoreEur: number | null
}

/** Daily KPI rows without CA TTC (CA HT and operational metrics stay here). CA TTC lives in {@link NetworkDashboardCaDailyAggregate}. */
export interface NetworkDashboardDailyAggregate {
  date: string
  totalCaHt: number
  totalBuyingPrice: number
  totalOneForOneOfferedBuyingPrice: number
  totalProductsSold: number
  totalCartsCount: number
  totalStolenCount: number
  totalStolenBuyingValue: number
  totalDestroyedCount: number
  totalDestroyedBuyingValue: number
  storeCount: number
  matureStoreCount: number
  matureCaHt: number
  matureBuyingPrice: number
  matureOneForOneOfferedBuyingPrice: number
  matureProductsSold: number
  matureCartsCount: number
  matureStolenCount: number
  matureStolenBuyingValue: number
  matureDestroyedCount: number
  matureDestroyedBuyingValue: number
  totalCaByHour: number[]
  totalCartsByHour: number[]
  totalArticlesSoldByHour: number[]
  matureCaByHour: number[]
  matureCartsByHour: number[]
  matureArticlesSoldByHour: number[]
}

/** CA TTC only (network dashboard). Dates may extend beyond {@link NetworkDashboardDailyAggregate} for chart rendering. */
export interface NetworkDashboardCaDailyAggregate {
  date: string
  totalCa: number
  matureCa: number
  storeCount: number
  matureStoreCount: number
}

/** Client-side merge of operational aggregates + CA rows for CA charts and blended metrics. */
export type NetworkDashboardMergedDailyAggregate = NetworkDashboardDailyAggregate &
  Pick<NetworkDashboardCaDailyAggregate, 'totalCa' | 'matureCa'>

export type NetworkDashboardCaChartGranularity = 'day' | 'week' | 'month'

export const NETWORK_DASHBOARD_COHORT_UNITS = ['day', 'week', 'month', 'year'] as const
export type NetworkDashboardCohortUnit = (typeof NETWORK_DASHBOARD_COHORT_UNITS)[number]

export interface NetworkDashboardCohortFilter {
  unit: NetworkDashboardCohortUnit
  value: number
}

export interface NetworkDashboardStoreWeeklyCa {
  storeId: number
  storeLabel: string
  periodCa: number
  comparisonCa: number
}

/** Live CA for all boutique stores: Paris “today” vs the same weekday last week, capped to the same wall-clock (see backend). */
export interface NetworkDashboardLiveStoreCa {
  todayParis: string
  sameDayLastWeekParis: string
  networkTotalCa: number
  networkSameDayLastWeekCa: number
  storeCas: NetworkDashboardStoreWeeklyCa[]
}

export interface NetworkDashboardCategoryPdm {
  categoryId: number
  name: string
  color: string | null
  caSharePercent: number
  uvcSharePercent: number
}

export interface NetworkDashboardTopProduct {
  reference: string
  label: string
  uvc: number
  previousUvc: number
  categoryIds: number[]
}

export interface NetworkDashboardHrMetrics {
  /** ETP moyen/boutique, présentiel + distanciel imputé. */
  avgETP: number
  /** Part présentiel de `avgETP`. */
  presentielEtp: number
  /** Part distanciel de `avgETP` : heures du pôle distanciel imputées au prorata du périmètre. */
  distancielEtp: number
  avgSalaryCostPerStore: number
  /** Planned hours on boutiques in scope (excluding the synthetic REMOTE store). */
  inPersonHoursTotal: number
  /** Planned hours on the single `REMOTE` store (télétravail). */
  remoteHoursTotal: number
}

// Store-age filter for the network dashboard.
//  - `default`: keep each card's historical behaviour (CA total uses all
//    stores, other cards use mature stores only).
//  - `all`: every card aggregates over every store.
//  - `mature`: every card aggregates over mature stores only (> 3 months as of period end).
//  - `young`: every card aggregates over young stores only (< 3 months as of period end).
export const NETWORK_DASHBOARD_STORE_AGE_FILTERS = [
  'default',
  'all',
  'less_than_3_months',
  'between_3_and_6_months',
  'between_6_and_12_months',
  'more_than_12_months',
] as const
export type NetworkDashboardStoreAgeFilter = (typeof NETWORK_DASHBOARD_STORE_AGE_FILTERS)[number]

export const DEFAULT_NETWORK_DASHBOARD_STORE_AGE_FILTER: NetworkDashboardStoreAgeFilter = 'default'

export interface NetworkDashboardAvailableStore {
  id: number
  label: string
  departmentCode: string | null
  /** Opening date (YYYY-MM-DD). Utilisé pour la cohorte / « boutiques ouvertes », pas le filtre ancienneté. */
  openingDate: string
  /**
   * Référence du filtre ancienneté (YYYY-MM-DD) = date de première vente (repli sur la date
   * d'ouverture si la boutique n'a pas encore vendu). C'est cette date que le filtre ancienneté
   * du dashboard Réseau & Boutiques compare à la fin de période.
   */
  ageReferenceDate: string
  /**
   * True for the synthetic « distanciel » store (store_type REMOTE). Only ever set when the
   * caller opts in (`includeRemote`, HR selectors) — grouped apart, at the bottom of the list.
   */
  isRemote?: boolean
}

/**
 * Real per-store cohort window. Only populated when KPIs are fetched in cohort mode —
 * `periodStart`/`periodEnd` in that case are synthetic (cohort-relative) and cannot
 * be used as calendar bounds for prorations.
 */
export interface NetworkDashboardCohortStoreWindow {
  storeId: number
  openingDate: string
  cohortEnd: string
}

export interface NetworkDashboardKpis {
  /** Inclusive main period (YYYY-MM-DD), echoed from the request / defaults. */
  periodStart: string
  periodEnd: string
  /** Inclusive comparison period computed server-side from the main period. */
  compStart: string
  compEnd: string
  /** Present when metrics are aligned on stores' opening-date cohorts instead of calendar dates. */
  cohort?: NetworkDashboardCohortFilter
  /**
   * Real per-store opening / cohort-end windows, exposed in cohort mode so the front
   * end can compute prorations against actual calendar dates (since `periodStart`
   * /`periodEnd` are synthetic placeholders relative to {@link cohort}).
   */
  cohortStoreWindows?: NetworkDashboardCohortStoreWindow[]
  dailyAggregates: NetworkDashboardDailyAggregate[]
  /** Real-calendar daily aggregates used by intraday views in cohort mode. */
  cohortIntradayAggregates?: NetworkDashboardMergedDailyAggregate[]
  /**
   * CA TTC per day for charts (possibly wider span than dailyAggregates).
   * KPI totals / comparison boxes still use {@link periodStart}–{@link periodEnd} vs {@link compStart}–{@link compEnd}.
   */
  caDailyAggregates: NetworkDashboardCaDailyAggregate[]
  /** How CA charts should bucket/labeled when spanning extended ranges. */
  caChartGranularity: NetworkDashboardCaChartGranularity
  /** Inclusive bounds used for the current-period CA chart series (may extend before {@link periodStart}). */
  caChartMainStart: string
  caChartMainEnd: string
  /** Inclusive bounds used for the comparison CA chart series (may extend before {@link compStart}). */
  caChartCompStart: string
  caChartCompEnd: string
  categoryPdm: NetworkDashboardCategoryPdm[]
  topProducts: NetworkDashboardTopProduct[]
  hrMetrics: NetworkDashboardHrMetrics
  availableStores: NetworkDashboardAvailableStore[]
}

export * from './network-casse-detail.js'
export * from './network-live-ca.js'
export * from './network-product-ranking.js'
export * from './network-store-ranking.js'
export * from './network-vol-detail.js'

export interface NearbyPlace {
  displayNameText: string
  primaryType: string | undefined
  formattedAddress?: string | undefined
  lat: number
  lng: number
  userRating?: number
  userRatingsTotal?: number
  websiteUri?: string
  inseeMatchScore?: number
  caYear?: number
  caAmount?: number
}

export interface AppAccordionItem<TLabel = unknown, TContent = unknown> {
  id: string
  label: TLabel
  content: TContent
}

export const GOOGLE_MAPS_PLACE_TYPES = [
  'bakery',
  'meal_delivery',
  'restaurant',
  'sandwich_shop',
  'butcher_shop',
  'convenience_store',
  'food_store',
  'grocery_store',
  'market',
  'supermarket',
] as const
export type GoogleMapsPlaceType = (typeof GOOGLE_MAPS_PLACE_TYPES)[number]

export const ACCEPTED_DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.jpg',
  '.jpeg',
  '.png',
  '.txt',
] as const
export type AcceptedDocumentExtension = (typeof ACCEPTED_DOCUMENT_EXTENSIONS)[number]

// ********************************************************* //
//                    LEGAL ENTITIES                         //
// ********************************************************* //

export interface LegalEntity extends TimestampsAndID {
  name: string
  description: string
  siret: string
}

// ********************************************************* //
//                         HR                                 //
// ********************************************************* //

// Collaborator data - represents an employee/collaborator
export interface Collaborator extends TimestampsAndID {
  // Admin account used to manage the collaborator in the BO
  adminId: number
  // Matricule of the collaborator
  matricule: string
  // Previous interim matricule, kept when contractType switches from 'interim' to 'cdd'/'cdi'
  interimMatricule: string | null
  // Role of the collaborator (e.g., 'adv', 'responsable-magasin')
  role: CollaboratorRole
  // Contract type (e.g., 'cdi', 'cdd', 'interim')
  contractType: ContractType
  // Status of the collaborator (e.g., 'actif', 'inactif')
  status: CollaboratorStatus
  // Gender ('homme' | 'femme'); null for legacy collaborators created before the field existed
  gender: CollaboratorGender | null
  // Birth date (ISO date); null for legacy collaborators
  birthDate: string | null
  // Professional status (CDI/CDD only: 'employe' | 'agent-de-maitrise' | 'cadre')
  professionalStatus: ProfessionalStatus | null
  // Company entry date (CDI/CDD only, distinct from the current contract start date)
  entryDate: string | null
  // Seniority date (CDI/CDD only)
  seniorityDate: string | null
  // Trial period end date (CDI/CDD only)
  trialPeriodEndDate: string | null
  // Phone number
  phone: string
  // Optional manager, stored as another collaborator id
  managerId: number | null
  // Weekly working minutes
  weeklyWorkingMinutes: number
  // Hourly cost in cents
  hourlyCost: number
  // S3 key for the collaborator profile picture
  profilePictureKey: string | null
  // Public URL computed from profilePictureKey
  profilePictureUrl: string | null
  // Manual override: when true, the inactivation cron does not touch `status`
  statusOverride: boolean
  // Access to the -15% offer ("sans carte")
  offerAccessEnabled: boolean
  // When true, the lifecycle does not auto-toggle the offer access flag
  offerAccessOverride: boolean
  // Computed (backend): id of the store of the active main store period.
  // Requires `activeMainStorePeriod` to be preloaded.
  mainStoreId?: number | null
  // Computed (backend): store of the active main store period.
  // Requires `activeMainStorePeriod` to be preloaded with its `store`.
  mainStore?: Store | null
  // Computed (backend): true if a formation period is currently active.
  // Requires `activeFormation` to be preloaded.
  isInFormation?: boolean
  // Optional relations (not always preloaded)
  admin?: Admin
  manager?: Collaborator
  stores?: Store[]
  workTimes?: CollaboratorWorkTime[]
  unavailabilities?: CollaboratorUnavailability[]
  equipment?: CollaboratorEquipment[]
  clothing?: CollaboratorClothing[]
  workShifts?: WorkShift[]
  // History of all main store assignments (most recent first)
  mainStorePeriods?: CollaboratorMainStore[]
  // Currently active main store period; null if none
  activeMainStorePeriod?: CollaboratorMainStore | null
  // History of multi-store assignment periods (most recent first)
  storePeriods?: CollaboratorStorePeriod[]
  // History of all formation periods (most recent first)
  formationPeriods?: CollaboratorFormation[]
  // Currently active formation period; null if none
  activeFormation?: CollaboratorFormation | null
  // History of all contracts (most recent first)
  contractPeriods?: CollaboratorContract[]
  // Currently active contract; null if none
  activeContract?: CollaboratorContract | null
  // Computed (backend) from the active contract; require `activeContract` preloaded
  interimAgencyId?: number | null
  contractStartDate?: string | null
  contractEndDate?: string | null
  titularisationDate?: string | null
  finDeCarenceDate?: string | null
  maxMaintienInterimDate?: string | null
  cumulativeInterimDays?: number | null
}

// Interim agency - referential for interim contracts (dropdown list)
export interface InterimAgency extends TimestampsAndID {
  name: string
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  // Soft-delete: archived agencies are hidden from the dropdown but kept for history
  isArchived: boolean
}

// Collaborator contract - one historised row per contract/mission.
// `endedAt === null` => active contract (at most one per collaborator,
// enforced by a partial unique index in DB). Interim contracts carry the
// regulatory derived dates (titularisation, fin de carence, max maintien).
export interface CollaboratorContract extends TimestampsAndID {
  collaboratorId: number
  contractType: ContractType
  // Required for interim, null otherwise
  interimAgencyId: number | null
  // "Durée hebdomadaire" carried per contract
  weeklyWorkingMinutes: number
  // Business contract bounds (ISO date strings). Start date optional for interim
  // (July fiche collab rules) — derived interim dates stay null without it.
  startDate: string | null
  endDate: string | null
  // Interim derived dates (ISO date strings), null for non-interim
  titularisationDate: string | null
  cumulativeInterimDays: number | null
  finDeCarenceDate: string | null
  maxMaintienInterimDate: string | null
  // Historisation / audit
  startedAt: string
  endedAt: string | null
  startedById: number | null
  endedById: number | null
  // Optional relations (not always preloaded)
  collaborator?: Collaborator
  interimAgency?: InterimAgency | null
  startedBy?: Admin | null
  endedBy?: Admin | null
}

// Collaborator work time - weekly work ranges for planning
export interface CollaboratorWorkTime extends TimestampsAndID {
  collaboratorId: number
  // Day of the week (e.g., 'monday', 'tuesday')
  dayOfWeek: WeekDay
  // Start time stored as "HH:mm"
  startTime: string
  // End time stored as "HH:mm"
  endTime: string
  // Optional relation (not always preloaded)
  collaborator?: Collaborator
}

// Collaborator unavailability - absences and unavailability periods
export interface CollaboratorUnavailability extends TimestampsAndID {
  collaboratorId: number
  // Reason for the absence
  reason: UnavailabityReason
  // Date range for the absence (ISO date string)
  startDate: string
  endDate: string
  // Optional time range when the absence is partial (stored as "HH:mm")
  startTime: string | null
  endTime: string | null
  // Optional relation (not always preloaded)
  collaborator?: Collaborator
}

// Flattened collaborator unavailability for weekly calendar display
export interface CollaboratorUnavailabilityCalendarItem {
  unavailabilityId: number
  collaboratorId: number
  collaboratorFullName: string
  reason: UnavailabityReason
  startDate: string
  endDate: string
  startTime: string | null
  endTime: string | null
}

// Collaborator equipment - assigned equipment items
export interface CollaboratorEquipment extends TimestampsAndID {
  collaboratorId: number
  // Type of equipment (e.g., 'computer', 'tablet', 'phone')
  type: EquipmentType
  // Date when the equipment was given (ISO date string)
  givenDate: string
  // Serial number of the equipment
  serialNumber: string
  // Brand of the equipment
  brand: string
  // Optional relation (not always preloaded)
  collaborator?: Collaborator
}

// Collaborator clothing - assigned clothing items
export interface CollaboratorClothing extends TimestampsAndID {
  collaboratorId: number
  // Type of clothing (e.g., 't-shirt', 'shirt')
  type: ClothingType
  // Size of the clothing (e.g., 'XS', 'S', 'M', 'L', 'XL', 'XXL')
  size: ClothingSize
  // Quantity of items
  quantity: number
  // Optional relation (not always preloaded)
  collaborator?: Collaborator
}

// Collaborator main store period - successive main store assignments.
// `endedAt === null` => active period (at most one per collaborator,
// enforced by a partial unique index in DB).
// During an active formation period, costs of all shifts are imputed to
// the store of the active main store period at the shift's date.
export interface CollaboratorMainStore extends TimestampsAndID {
  collaboratorId: number
  storeId: number
  // ISO datetime when the period started
  startedAt: string
  // ISO datetime when the period ended; null if still active
  endedAt: string | null
  // Audit: admin who opened / closed the period
  startedById: number | null
  endedById: number | null
  // Optional relations (not always preloaded)
  collaborator?: Collaborator
  store?: Store
  startedBy?: Admin | null
  endedBy?: Admin | null
}

// Multi-store assignment period (historised) — same shape as CollaboratorMainStore;
// the CURRENT assignment set is still carried by the collaborator_stores pivot.
export interface CollaboratorStorePeriod extends TimestampsAndID {
  collaboratorId: number
  storeId: number
  startedAt: string
  endedAt: string | null
  startedById: number | null
  endedById: number | null
  collaborator?: Collaborator
  store?: Store
  startedBy?: Admin | null
  endedBy?: Admin | null
}

// Onboarding formation - happens once per collaborator, ever.
// `startedAt`..`endedAt` is the DECLARED period: midnight and 23:59:59.999 Europe/Paris on the
// first and last day. `endedAt === null` means no term was declared, NOT "still running": a
// declared end may be in the future.
export interface CollaboratorFormation extends TimestampsAndID {
  collaboratorId: number
  startedAt: string
  endedAt: string | null
  // Both carry the last editor, so `endedById` duplicates `startedById` today.
  startedById: number | null
  endedById: number | null
  // Optional relations (not always preloaded)
  collaborator?: Collaborator
  startedBy?: Admin | null
  endedBy?: Admin | null
}

// Work shift - scheduled work shifts for store scheduling
export interface WorkShiftClock extends TimestampsAndID {
  workShiftId: number | null
  collaboratorId: number
  loggedById: number
  action: WorkShiftClockAction
  clockedAt: string
  temoinFileKey: string | null
  temoinFileUrl?: string | null
  comment: string | null
  // Optional relations (not always preloaded)
  workShift?: WorkShift
  admin?: Admin
}

export interface AttendanceHistoryItem extends WorkShiftClock {
  collaborator?: Collaborator
}

export interface WorkShift extends TimestampsAndID {
  collaboratorId: number
  storeId: number
  // Planned schedule (ISO date string)
  date: string
  // Start time stored as "HH:mm"
  startTime: string
  endTime: string
  validatedAt?: string | null
  validatedById?: number | null
  validatedBy?: Admin | null
  validationStatus?: 'pending' | 'validated'
  validatedStartTime?: string | null
  validatedEndTime?: string | null
  actualStartTime?: string | null
  actualEndTime?: string | null
  // Pointage retenu (franchise d'entrée appliquée selon la règle de coupure 45 min)
  retainedStartTime?: string | null
  retainedEndTime?: string | null
  // Overnight shift linking: child shift points to its parent
  parentShiftId?: number | null
  // Convenience id of the child shift (present on parent of an overnight shift)
  childShiftId?: number | null
  // Optional child shift (present on parent of an overnight shift)
  childShift?: WorkShift | null
  // Optional parent shift (present on child of an overnight shift)
  parentShift?: WorkShift | null
  // Optional clock events
  clockValues?: WorkShiftClock[]
  isInFormation?: boolean
  // Créneau dont le collaborateur est absent (chevauche une CollaboratorUnavailability) :
  // affiché « en absence » sur le planning, ses heures sont exclues au prorata des totaux.
  isInAbsence?: boolean
  // Plages HH:mm du créneau couvertes par l'absence (pour l'affichage, ex. 09:00 - 12:00).
  absenceRanges?: { start: string; end: string }[]
  // Optional relations (not always preloaded)
  collaborator?: Collaborator
  store?: Store
}

/**
 * Bloc de transport (déplacement payé entre 2 boutiques différentes, coupure < 45 min) à
 * afficher sur le planning. Modèle dérivé : calculé côté backend, non stocké.
 */
export interface TransportSegmentDisplay {
  collaboratorId: number
  date: string // YYYY-MM-DD
  startTime: string // HH:mm (fin du shift précédent)
  endTime: string // HH:mm (début du shift suivant)
  fromStoreId: number
  toStoreId: number
  fromStoreLabel: string
  toStoreLabel: string
}

export interface FetchTransportSegmentsParams {
  storeId: number
  dateStart: string
  dateEnd: string
}

export interface HrAnalyticsResponse {
  period: {
    dateStart: string
    dateEnd: string
  }
  headcount: {
    total: number
    onSite: number
    remote: number
  }
  workedHours: {
    averagePerWeek: number
    averagePerDay: Record<WeekDay, number>
  }
  salaryCost: {
    // Total salary cost in integer cents.
    amount: number
    histogram: {
      date: string
      // Salary cost bucket in integer cents.
      amount: number
    }[]
  }
}

export type HrSalesPeaksResponse = Record<
  DayOfWeek,
  {
    time: string
    cartsSold: number
    cartsPct: number
  }[]
>

export interface HrStoreWeeklyMetricsResponse {
  period: {
    timezone: 'Europe/Paris'
    startDate: string
    endDate: string
    excludesToday: boolean
  }
  store: {
    storeId: number
    workedHours: {
      amount: number
      // Salary cost in integer cents.
      salaryCost: number
    }
    // Physical presence hours = total des heures où quelqu'un était sur ce
    // store (executionStoreId === storeId), peu importe à qui le coût est
    // imputé. Diffère de `workedHours.amount` quand des shifts en formation
    // ont migré leur coût entre stores.
    physicalPresenceHours: number
    remoteHours: number
    // Salary cost rate in cents per uncovered remote hour.
    remoteHourlySalaryCost: number
    // Revenue in integer cents.
    revenue: { caTtc: number }
    formationHoursCovered: number
    formationCostsImputed: number
    formationImputedHours: number
    formationCoveredCollabs: {
      collaboratorId: number
      firstName: string
      lastName: string
      profilePictureUrl: string | null
      mainStoreName: string
      hours: number
    }[]
    formationImputedCollabs: {
      collaboratorId: number
      firstName: string
      lastName: string
      profilePictureUrl: string | null
      executionStoreName: string
      hours: number
      cost: number
    }[]
  }
  global: {
    workedHours: {
      amount: number
      // Salary cost in integer cents.
      salaryCost: number
    }
    // Revenue in integer cents.
    revenue: { caTtc: number }
  }
  comparisons: {
    // Percentage change vs same store's previous week (S-1). null when no previous data.
    previousWeek: {
      presentielCostPct: number | null
      distancielCostPct: number | null
      totalStaffCostPct: number | null
    }
    // Percentage diff vs average of OTHER boutiques with >= 1 shift this week. null when no other boutique.
    network: {
      presentielCostPct: number | null
      distancielCostPct: number | null
      totalStaffCostPct: number | null
    }
    // Staff cost / previous complete week revenue ratio.
    staffCostRevenueRatio: {
      storeRatio: number | null
      networkRatio: number | null
    }
  }
}

export interface HrRemoteStorePeriodMetricsResponse {
  period: {
    timezone: 'Europe/Paris'
    startDate: string
    endDate: string
    excludesToday: boolean
  }
  storeId: number
  // Heures effectuées physiquement sur ce store remote par des collabs en
  // formation rattachés ailleurs. Déjà incluses dans `day.hours + night.hours` ;
  // exposées séparément pour la traçabilité (le coût correspondant a été
  // imputé à la mainStore du collaborateur, pas à ce store remote).
  formationHoursCovered: number
  day: {
    hours: number
    // Salary cost in integer cents.
    salaryCost: number
  }
  night: {
    hours: number
    // Salary cost in integer cents.
    salaryCost: number
  }
}

// ----------------------------------------------------------------------- //
//                          HR DASHBOARD (réseau)                          //
// ----------------------------------------------------------------------- //

// Network-level KPIs for the HR dashboard, over a rolling date window.
export interface HrDashboardOverviewResponse {
  period: {
    timezone: 'Europe/Paris'
    dateStart: string
    dateEnd: string
  }
  effectif: {
    // Full-time equivalents (ETP) across the network.
    totalEtp: number
    presentielEtp: number
    distancielEtp: number
    // Percentage change of total ETP vs the previous equivalent window. null when no prior data.
    vsPreviousPct: number | null
    // Signed absolute ETP delta vs the previous equivalent window (for the "+N vs M-1" label).
    vsPreviousDeltaEtp: number
  }
  fraisPersonnel: {
    // Payroll cost over the window, integer cents, HORS coût des créneaux de formation (#679).
    amountCents: number
    // Average cost per ETP, integer cents. Numérateur BRUT (formation comprise) : son dénominateur
    // garde les heures de formation, donc `amountCents / totalEtp` ne redonne PAS ce champ.
    coutMoyenParEtpCents: number
    // Average cost per boutique, integer cents, hors coût de formation comme `amountCents`.
    fpParBoutiqueCents: number
    // Percentage change vs the previous equivalent window, sur la même base qu'`amountCents`.
    // null when no prior data.
    vsPreviousPct: number | null
  }
  coutMoyenHeure: {
    // Average cost per worked hour, integer cents.
    presentielCentsPerHour: number
    distancielCentsPerHour: number
  }
  repartition: {
    presentielHours: number
    distancielHours: number
    // Cost split, integer cents.
    presentielValueCents: number
    distancielValueCents: number
  }
  // « NB heures consommées » : heures TRAVAILLÉES (méthode Réseau & Boutiques), à distinguer de
  // `repartition` qui exprime la COUVERTURE. Présentiel = heures travaillées en boutique (somme des
  // heures de chaque salarié présent, chevauchements comptés). Distanciel = heures du pool REMOTE
  // imputées au périmètre. Sert la carte « NB heures consommées » (la jauge Répartition, elle, reste
  // sur la couverture `repartition`).
  heuresConsommees: {
    presentielHours: number
    distancielHours: number
  }
}

export interface HrDashboardTrendPoint {
  // ISO date of the day this point represents (one point per day of the rolling week).
  weekStart: string
  value: number
}

// Daily time-series for the two trend charts: the current rolling week (Lun..Dim).
// `current` is this week's 7 days; `previous` is the same weekdays one week back (S-1).
export interface HrDashboardTrendsResponse {
  period: {
    timezone: 'Europe/Paris'
    dateStart: string
    dateEnd: string
  }
  // Frais de personnel / CA, as a ratio (0.084 = 8,4 %).
  fraisPersonnelSurCa: {
    current: HrDashboardTrendPoint[]
    previous: HrDashboardTrendPoint[]
    // Ratio agrégé Σ(coût imputé) / Σ(CA HT) sur la semaine — méthodo canonique identique à la modale
    // « Frais de personnel / CA ». C'est LE chiffre de la carte (la moyenne des `current` divergerait,
    // pondération par jour ≠ par CA). `ratioPrevious` = même agrégat sur S-1 (pour la patate vs S-1).
    ratio: number
    ratioPrevious: number
  }
  // Average ETP per boutique.
  etpMoyenBoutique: {
    current: HrDashboardTrendPoint[]
    previous: HrDashboardTrendPoint[]
  }
}

export interface HrDashboardCoverageSlotCell {
  // Slot CA / day 24h CA (0..1); null when the day's CA is 0.
  caShare: number | null
  // Average bodies present during the slot (Σ overlap minutes / 60).
  etp: number
  // Average number of distinct boutiques that received ≥1 delivery in that hour (Σ store-days /
  // occurrences of the weekday). Drives the "Liv." bar intensity; 0 when no delivery.
  livraison: number
  // Effectif split for the "Eff." bar (avg bodies per weekday occurrence):
  //  - presentielEtp = BOUTIQUE (physical) shifts in the slot.
  //  - distancielEtp = REMOTE shifts in the slot, imputed to the scope pro-rata of its share of the
  //    network's uncovered hours (same model as getHrDashboardOverview).
  // The bar renders présentiel (bright orange) vs distanciel (light orange) = their proportions.
  presentielEtp: number
  distancielEtp: number
}

export interface HrDashboardCoverageWeekday {
  // ISO weekday: 1 = Monday .. 7 = Sunday.
  weekday: number
  // 24 cells, slot order 00H .. 23H.
  cells: HrDashboardCoverageSlotCell[]
}

// Planning coverage heatmap: 24 one-hour slots (rows) x 7 weekdays (columns),
// each column split into CA% (green) and effectif ETP (orange). All occurrences of
// each weekday within the chosen period are aggregated (CA summed, ETP averaged per
// occurrence). Data is for the selected store, or aggregated across all boutiques
// when none is selected.
export interface HrDashboardCoverageResponse {
  period: {
    timezone: 'Europe/Paris'
    dateStart: string
    dateEnd: string
  }
  storeId: number | null
  // 24 slot labels: "00H" .. "23H".
  slots: string[]
  // Always 7 entries, weekday 1 (Mon) .. 7 (Sun).
  weekdays: HrDashboardCoverageWeekday[]
  // Part des heures-boutique OUVERTES couvertes par ≥1 présence physique (0..1). Header de la
  // modale détail « Couverture Planning » (présence plafonnée à l'offre d'ouverture par créneau).
  coverageRate: number
  // Nombre de boutiques du périmètre agrégé — diviseur pour ramener présentiel/distanciel (totaux
  // cumulés) en moyennes PAR boutique dans les tooltips « Eff. ».
  storeCount: number
}

export interface HrEffectifDetailTrendPoint {
  weekStart: string
  value: number
}

// Detailed breakdown behind the "Effectif total réseau" card (modal).
export interface HrEffectifDetailResponse {
  period: {
    timezone: 'Europe/Paris'
    dateStart: string
    dateEnd: string
  }
  summary: {
    totalEtp: number
    vsPreviousPct: number | null
    etpMoyenParBoutique: number
    nombreContrats: number
  }
  // Average composition of one boutique, by job type.
  composition: {
    totalEtp: number
    totalCostCents: number
    rows: { label: string; etp: number; costCents: number; pct: number }[]
  }
  // ETP split by contract status (weighted by contract hours, not headcount).
  parStatutContractuel: {
    totalEtp: number
    rows: { label: string; etp: number; pct: number }[]
  }
  // ETP split by job type (weighted by contract hours, not headcount).
  parTypePoste: {
    totalEtp: number
    rows: { label: string; etp: number; pct: number }[]
  }
  // Total-ETP series: current ("Valeur") and S-1. `granularity` (jour/semaine/mois, aligné sur le
  // Dashboard Réseau) déduite de la période ; chaque point porte le début de bucket dans `weekStart`.
  trend: {
    granularity: NetworkDashboardCaChartGranularity
    valeur: HrEffectifDetailTrendPoint[]
    valeurPrevious: HrEffectifDetailTrendPoint[]
  }
}

export interface HrFraisDetailTrendPoint {
  weekStart: string
  // Salary cost for the week, integer cents, hors coût de formation (#679).
  value: number
}

// Detailed breakdown behind the "Frais de personnel réseau" card (modal).
export interface HrFraisDetailResponse {
  period: {
    timezone: 'Europe/Paris'
    dateStart: string
    dateEnd: string
  }
  summary: {
    // Hors coût des créneaux de formation (#679), comme les ventilations ci-dessous.
    amountCents: number
    vsPreviousPct: number | null
    moyParBoutiqueCents: number
    // Numérateur BRUT (formation comprise) : `totalEtp` garde les heures de formation.
    coutParEtpCents: number
    totalEtp: number
  }
  // Salary-cost series: current ("FP réel") and S-1. `granularity` (jour/semaine/mois, aligné sur le
  // Dashboard Réseau) déduite de la période ; chaque point porte le début de bucket dans `weekStart`.
  evolution: {
    granularity: NetworkDashboardCaChartGranularity
    reel: HrFraisDetailTrendPoint[]
    previous: HrFraisDetailTrendPoint[]
  }
  // Salary cost per boutique, sorted desc, with the network average. Hors coût de formation
  // (#679) : une boutique qui n'héberge que de la formation reste listée, à 0 €.
  masseSalarialeParBoutique: {
    moyenneCents: number
    rows: { storeName: string; storeShortLabel: string | null; amountCents: number }[]
  }
  // Cost split by job type.
  repartitionParPoste: {
    totalCents: number
    rows: { label: string; count: number; amountCents: number; pct: number }[]
  }
  // Cost split by contract status.
  fpParStatut: {
    totalCents: number
    rows: { label: string; count: number; amountCents: number; pct: number }[]
  }
  // FP/CA ratio per boutique, sorted desc, with the network average.
  fpCaParBoutique: {
    moyenneRatio: number
    rows: { storeName: string; storeShortLabel: string | null; ratio: number }[]
  }
  // Aggregates behind the "Frais de personnel / CA" modal. CA HT sourced from daily_store_kpis
  // over the window; ratio = MS / CA (decimal).
  fpCa: {
    caReseauCents: number // CA HT, current window, boutiques in scope
    caReseauPreviousCents: number // CA HT, S-1 window
    msReseauCents: number // masse salariale, current window — REMOTE imputé + boutiques matures,
    // donc ≠ summary.amountCents malgré la même exclusion du coût de formation
    msReseauPreviousCents: number // masse salariale, S-1 window
    ratio: number // FP/CA current (decimal, 0.084 = 8.4 %)
    ratioPrevious: number // FP/CA S-1
    // FP/CA ratio series (value = ratio decimal): current ("Valeur") and S-1. `granularity` alignée
    // sur le Dashboard Réseau ; chaque point porte le début de bucket dans `weekStart`.
    evolution: {
      granularity: NetworkDashboardCaChartGranularity
      reel: HrFraisDetailTrendPoint[]
      previous: HrFraisDetailTrendPoint[]
    }
  }
}

// OK = planning publié ; ATTENTE = planning non publié (validé ou brouillon) ;
// ALERTE = aucun planning.
export type PlanningStatus = 'ok' | 'attente' | 'alerte'

// Per-boutique planning status. `period` describes the assessed week S+1 (the next week's
// planning); ETP comes from S+1, the FP/CA ratio uses S-1 realised revenue (master 464).
export interface HrDashboardPlanningStatusResponse {
  period: {
    timezone: 'Europe/Paris'
    weekStart: string
    weekEnd: string
  }
  counts: {
    ok: number
    attente: number
    alerte: number
  }
  // Target bands the status/colors are derived from (ticket 536). `fpCa` bounds are ratios
  // (0.12 = 12 %). Provided so the front shows the cibles without hard-coding them.
  targets: {
    etp: { min: number; max: number }
    fpCa: { min: number; max: number }
  }
  stores: {
    storeId: number
    storeName: string
    // Short store label (≤ 30 chars) when set, for compact display; null falls back to storeName.
    storeShortLabel: string | null
    status: PlanningStatus
    // Full-time equivalents scheduled for the assessed (S+1) week.
    etp: number
    // S+1 staff cost (hors coût de formation, #679) / S-1 revenue, as a ratio (0.14 = 14 %).
    // null when no revenue.
    fpCaRatio: number | null
    // true when the store opened ≥ 3 months before the assessed week (mature); false when it
    // opened within the last 3 months. Drives the ">3 mois" / "<3 mois" grouping in the panel.
    isOver3Months: boolean
  }[]
}

export interface PublishPlanningSmsResponse {
  status: 'ok' | 'partial'
  period: {
    dateStart: string
    dateEnd: string
    timezone: 'Europe/Paris'
  }
  storeId: number
  collaboratorsWithShifts: number
  smsSent: number
  failed: number
  message: string
}

export interface PublicPlanningChildShift {
  id: number
  date: string // YYYY-MM-DD
  startTime: string // HH:mm:ss
  endTime: string
  actualStartTime: string | null
  actualEndTime: string | null
}

export interface PublicPlanningShift {
  id: number
  collaboratorId: number
  date: string
  startTime: string
  endTime: string
  actualStartTime: string | null
  actualEndTime: string | null
  validationStatus: 'pending' | 'validated'
  parentShiftId: number | null
  childShift: PublicPlanningChildShift | null
}

export interface PublicPlanningResponse {
  collaborator: {
    id: number
    firstName: string | null
    lastName: string | null
  }
  collaborators: { id: number; firstName: string | null; lastName: string | null }[]
  store: {
    id: number
    label: string
    address: {
      street: string
      postalCode: string
      city: string
    } | null
  }
  period: {
    weekStart: string // YYYY-MM-DD
    weekEnd: string
    timezone: 'Europe/Paris'
  }
  shifts: PublicPlanningShift[]
}

export const SMS_TRACKING_PERIODS = ['7d', '30d', '90d'] as const
export type SmsTrackingPeriod = (typeof SMS_TRACKING_PERIODS)[number]

export const SMS_TRACKING_STATUSES = ['all', 'read', 'unread'] as const
export type SmsTrackingStatus = (typeof SMS_TRACKING_STATUSES)[number]

export interface SmsTrackingItem {
  id: number
  collaborator: {
    id: number
    firstName: string | null
    lastName: string | null
    phone: string
  }
  store: {
    id: number
    label: string
  }
  weekStart: string // YYYY-MM-DD
  weekEnd: string // YYYY-MM-DD
  sentAt: string // ISO datetime
  firstViewedAt: string | null // ISO datetime
  lastViewedAt: string | null // ISO datetime
  viewCount: number
  body: string
  revokedAt: string | null
}

export interface SmsTrackingListResponse {
  items: SmsTrackingItem[]
  total: number
  page: number
  pageSize: number
}

export interface SmsTrackingMetric {
  current: number
  previous: number
}

export interface RelaunchUnreadSmsResponse {
  sent: number
  failed: number
  skipped: number
}

export interface SmsTrackingStatsResponse {
  period: {
    current: { dateStart: string; dateEnd: string }
    previous: { dateStart: string; dateEnd: string }
  }
  sent: SmsTrackingMetric
  read: SmsTrackingMetric
  unread: SmsTrackingMetric
  readRate: SmsTrackingMetric // 0..1
}

export interface CopyPlanningWeekData {
  storeId: number
  sourceWeekNumber: number
  sourceYear: number
  targetWeekNumber: number
  targetYear: number
}

export interface CopyPlanningWeekPeriod {
  weekNumber: number
  year: number
  dateStart: string
  dateEnd: string
}

export interface CopyPlanningWeekSkippedShift {
  sourceWorkShiftId: number
  collaboratorId: number
  collaboratorName: string
  targetDate: string
  startTime: string
  endTime: string
  reason: string
}

export interface CopyPlanningWeekResponse {
  status: 'ok' | 'partial'
  storeId: number
  sourcePeriod: CopyPlanningWeekPeriod
  targetPeriod: CopyPlanningWeekPeriod
  totalSourceShifts: number
  created: number
  skipped: number
  skippedShifts: CopyPlanningWeekSkippedShift[]
  message: string
}

// ********************************************************* //
//                  PLANNING CONFIG                          //
// ********************************************************* //

export interface PlanningGlobalConfig extends TimestampsAndID {
  maxWeeklyHours: number
  maxDailyHours: number
  maxShiftDurationHours: number
  maxConsecutiveWorkDays: number
  minWeeklyRestHours: number
  breakTriggerHours: number
  breakDurationMinutes: number
  maxDailyBreaks: number
  nightStartMinutes: number
  nightEndMinutes: number
  nightMultiplierCenti: number
  weekendMultiplierCenti: number
  nightWeekendMultiplierCenti: number
  // Dashboard RH cibles: ETP band + FP/CA ratio band (e.g. 0.12 = 12 %) driving planning status.
  etpTargetMin: number
  etpTargetMax: number
  fpCaTargetMin: number
  fpCaTargetMax: number
  // ok <-> attente deadline: weekday (1 = Monday .. 7 = Sunday) + minutes from midnight.
  okAttenteDeadlineWeekday: number
  okAttenteDeadlineMinutes: number
}

export interface PlanningStoreStaffingRule extends TimestampsAndID {
  storeId: number
  minTurnover: number
  maxTurnover: number
  staffCount: number
}

export interface PlanningStoreConfig extends TimestampsAndID {
  storeId: number
  lunchBreakMinutes: number
  staffingRules?: PlanningStoreStaffingRule[]
}

export interface PlanningConfig {
  global: PlanningGlobalConfig
  storeConfigs: PlanningStoreConfig[]
}

export interface ConstraintCheckItem {
  collaboratorId: number
  collaboratorName: string
  value: number
  limit: number
  respected: boolean
  detail: string
}

export interface ConstraintsReport {
  maxWeeklyHours: ConstraintCheckItem[]
  maxShiftDuration: ConstraintCheckItem[]
  maxConsecutiveWorkDays: ConstraintCheckItem[]
  minBreakBetweenShifts: ConstraintCheckItem[]
}

export interface PlanningLastOptimization {
  id: number
  status: PlanningOptimizationStatus
  startedAt: string
  finishedAt: string | null
  createdShifts: number | null
  updatedShifts: number | null
  adminFirstName: string
  adminLastName: string
}

export type OptimizePlanningSkippedKind = 'create' | 'extend' | 'overnight-child'

export interface OptimizePlanningSkippedShift {
  kind: OptimizePlanningSkippedKind
  collaboratorId: number
  collaboratorName: string
  storeId: number
  date: string
  startTime: string
  endTime: string
  reason: string
}

// ********************************************************* //
//                    XLSX IMPORT                            //
// ********************************************************* //
export interface RowError {
  reference: string
  message: string
}

// When at least one row is valid, this is the response
export interface XlsxImportResponse {
  message: string
  errors: RowError[]
  updated: number
  created: number
  failed: number
}

export interface ParsingRowError {
  row: number
  message: string
}

// When there is a global xlsx parsing error, this is the response
export interface XlsxImportError {
  message: string
  errors: ParsingRowError[]
}

// Résultat de l'import de masse des collaborateurs (MAJ par matricule).
export interface CollaboratorImportResult {
  // Nombre de fiches mises à jour.
  updated: number
  // Lignes en erreur (matricule inconnu, parsing…).
  errors: RowError[]
  // Alertes « règles de forme » non bloquantes (majuscules, capitalisation…).
  warnings: RowError[]
}

export * from './store-project-fields.js'

// ********************************************************* //
//                       STREAM                              //
// ********************************************************* //

export * from './stream/stream.js'

// ********************************************************* //
//                       ENUMS                               //
// ********************************************************* //

export * from './enums/index.js'

// ********************************************************* //
//                       CONSTANTS                           //
// ********************************************************* //

export * from './attendance-times.js'
export * from './clock-comment.js'
export * from './constants.js'
export * from './rbac.js'
export * from './rbac.machine.js'
export * from './rbac.v2.js'
export * from './schemas/cameraWorker.js'
export * from './schemas/dispatchSimulation.js'
export * from './schemas/platformStocks.js'
export * from './schemas/reapproSimulation.js'

// ********************************************************* //
//                  INTEREST ZONE RUNS                       //
// ********************************************************* //

export interface InterestZoneFilter {
  categoryId: number | null
  minCount: number
}

export interface InterestZoneRunParams {
  maxRadiusKm: number
  minPopulation: number
  filters: InterestZoneFilter[]
}

export interface InterestZoneRunStats {
  seedsEligible: number
  zonesBuilt: number
  zonesDiscarded: number
  totalIrisUsed: number
  totalPopulation: number
  pop: { min: number; max: number; mean: number; median: number }
  radiusMeters: { min: number; max: number; mean: number; median: number }
}

export type InterestZoneRunStatus = 'pending' | 'completed' | 'failed'

export interface InterestZoneRun {
  id: number
  key: string
  status: InterestZoneRunStatus
  params: InterestZoneRunParams
  stats: InterestZoneRunStats | null
  fileKey: string | null
  error: string | null
  createdAt: string
  completedAt: string | null
}

export interface InterestZoneRunWithUrl extends InterestZoneRun {
  fileUrl: string | null
}

export interface InterestZoneFeatureProperties {
  zoneId: number
  seedIrisCode: string
  irisCount: number
  population: number
  radiusMeters: number
  centroidLat: number
  centroidLng: number
  poisByCategoryId: Record<string, number>
  poisTotal: number
  maxPopIrisCode: string
  maxPopIrisName: string
  maxPopIrisPop: number
  maxPopIrisGeometry: GeoJSON.Polygon | GeoJSON.MultiPolygon
}

export type InterestZoneFeatureCollection = GeoJSON.FeatureCollection<
  GeoJSON.Polygon | GeoJSON.MultiPolygon,
  InterestZoneFeatureProperties
>

// ********************************************************* //
//                     PLATFORM STOCKS                       //
// ********************************************************* //

export interface PlatformStockImportResult {
  updated: number
  errors: { reference: string; message: string }[]
}

export interface CameraSlot {
  id: string
  storeId: number | null
  cameraId: number | null
  x: number
  y: number
  w: number
  h: number
}

export interface CameraView {
  id: string
  name: string
  cols: number
  rows: number
  slots: CameraSlot[]
}

export interface CameraViewListItem {
  id: string
  name: string
}

export interface CreateCameraViewInput {
  name: string
  cols: number
  rows: number
  slots: CameraSlot[]
}

export interface UpdateCameraViewInput {
  name?: string
  cols?: number
  rows?: number
  slots?: CameraSlot[]
}
